/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	Component,
	Injectable
} from '@angular/core';
import {
	Router
} from '@angular/router';
import {
	EntityService
} from '@entity/services/entity.service';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	ObjectHelper
} from '@shared/helpers/object.helper';
import {
	StringHelper
} from '@shared/helpers/string.helper';
import {
	IDynamicComponentContext
} from '@shared/interfaces/application-objects/dynamic-component-context.interface';
import {
	IEntityNavigationDefinition
} from '@shared/interfaces/application-objects/entity-navigation-definition.interface';
import {
	IEntityNavigationRequest
} from '@shared/interfaces/application-objects/entity-navigation-request.interface';
import {
	ResolverService
} from '@shared/services/resolver.service';

/* eslint-enable max-len */

/**
 * A singleton service for centralized entity navigation logic.
 *
 * @export
 * @class NavigationService
 */
@Injectable({
	providedIn: 'root'
})
export class NavigationService
{
	/**
	 * Creates an instance of NavigationService.
	 *
	 * @param {ResolverService} resolver
	 * The resolver service for dynamic component resolution.
	 * @param {Router} router
	 * The Angular router for navigation.
	 * @param {EntityService} entityService
	 * The entity service for retrieving navigation promise strings.
	 * @memberof NavigationService
	 */
	public constructor(
		public resolver: ResolverService,
		private readonly router: Router,
		private readonly entityService: EntityService)
	{
	}

	/**
	 * Gets navigation definition for an entity instance.
	 *
	 * Retrieves the navigation promise string from the entity definition,
	 * executes it as a function with the provided context, and
	 * returns the resulting navigation definition.
	 *
	 * @remarks We offer a source resolver service and the navigation request
	 * in the navigation promise in the entity definition. This functionality
	 * requires global search compatibility which has no set source, so all
	 * navigation promises must be compatible with this.
	 *
	 * @async
	 * @param {IEntityNavigationRequest} navigationRequest
	 * The navigation request containing navigation parameters.
	 * @returns {Promise<IEntityNavigationDefinition>}
	 * A promise containing the navigation definition.
	 * @memberof NavigationService
	 */
	public async getEntityNavigationDefinition(
		navigationRequest: IEntityNavigationRequest):
		Promise<IEntityNavigationDefinition>
	{
		const navigationPromiseString: string =
			await this.entityService
				.getEntityNavigationDefinition(
					navigationRequest.entityType);

		if (AnyHelper.isNullOrWhitespace(navigationPromiseString))
		{
			throw new Error(
				'Navigation promise not found for entity type: '
					+ navigationRequest.entityType);
		}

		const navigationDefinition: IEntityNavigationDefinition =
			await StringHelper.transformToDataPromise(
				navigationPromiseString,
				<IDynamicComponentContext<Component, any>>
				{
					source: <Component>this,
					data: navigationRequest
				});

		return navigationDefinition;
	}

	/**
	 * Navigates to an entity using the provided navigation definition.
	 *
	 * @async
	 * @param {IEntityNavigationDefinition} navigationDefinition
	 * The navigation definition containing routing information.
	 * @param {boolean} isViewOnly
	 * Indicates if the navigation should be to a view-only layout.
	 * @returns {Promise<void>}
	 * A promise that resolves when navigation is complete.
	 * @memberof NavigationService
	 */
	public async navigateToEntityDefinition(
		navigationDefinition: IEntityNavigationDefinition,
		isViewOnly: boolean = false): Promise<void>
	{
		const isGeneratedLayout: boolean =
			navigationDefinition.layoutType ===
				AppConstants.layoutTypes.generated;

		const viewType: string =
			isViewOnly || isGeneratedLayout
				? AppConstants.viewTypes.view
				: AppConstants.viewTypes.edit;

		const routeData: any =
			{
				layoutType: navigationDefinition.layoutType,
				...navigationDefinition.additionalRouteData
			};

		await this.router.navigate(
			[
				navigationDefinition.baseUrl,
				navigationDefinition.entityTypeGroup,
				viewType,
				navigationDefinition.id
			],
			{
				queryParams: {
					routeData:
						ObjectHelper.mapRouteData(
							routeData)
				}
			});
	}
}